import { tenantConfig } from './tenant-config';

export { tenantConfig } from './tenant-config';
export type { TenantConfig, TenantLanding } from './tenant-config';

/** Base URL for backend API calls. Empty string for local dev (uses Vite proxy). */
export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export function getAppName() { return tenantConfig.identity.name; }
export function getAppDescription() { return tenantConfig.identity.tagline; }
export const APP_NAME = tenantConfig.identity.name;
export const APP_DESCRIPTION = tenantConfig.identity.tagline;

export const POLYGON_CHAIN_ID = tenantConfig.chain.chainId;

export const CONTRACTS = {
  SLGNS: tenantConfig.contracts.slgns,
  LGNS:  tenantConfig.contracts.lgns,
  DAI:   tenantConfig.contracts.dai,
  USDT:  tenantConfig.contracts.usdt,
} as const;

export const WALLETS = {
  APEX_COMMAND: (import.meta.env.VITE_APEX_COMMAND_WALLET ?? '') as `0x${string}`,
  SUB_1:        (import.meta.env.VITE_SUB_WALLET_1 ?? '')         as `0x${string}`,
  SUB_2:        (import.meta.env.VITE_SUB_WALLET_2 ?? '')         as `0x${string}`,
  SUB_3:        (import.meta.env.VITE_SUB_WALLET_3 ?? '')         as `0x${string}`,
} as const;

export const ORIGIN_LGNS_URL = tenantConfig.links.website;
export const ORIGIN_REFERRAL = import.meta.env.VITE_ORIGIN_REFERRAL ?? '';
export const ORIGIN_LGNS_INVITE_URL = ORIGIN_REFERRAL
  ? `${ORIGIN_LGNS_URL}/#/invite?code=${ORIGIN_REFERRAL}`
  : ORIGIN_LGNS_URL;

export const COMMUNITY_LINKS = {
  TELEGRAM:  tenantConfig.links.telegram,
  WHATSAPP:  tenantConfig.links.whatsapp,
  FACEBOOK:  tenantConfig.links.facebook,
  DISCORD:   tenantConfig.links.discord,
} as const;

export const PLATFORM_LINKS = {
  LGNS_BACKOFFICE: tenantConfig.links.backoffice,
} as const;

export const AUDIT_LINKS = {
  CERTIK: tenantConfig.links.certikAudit,
} as const;

export const YIELD_RATES = {
  REBASE_YIELD:   tenantConfig.yield.rebaseYield,
  REBASES_PER_DAY: tenantConfig.yield.rebasesPerDay,
  DAILY_APY:      Math.pow(1 + tenantConfig.yield.rebaseYield, tenantConfig.yield.rebasesPerDay) - 1,
  OVERRIDE_BONUS: tenantConfig.yield.overrideBonus,
  CONSERVATIVE:   tenantConfig.yield.conservativeRate,
  OPTIMISTIC:     tenantConfig.yield.optimisticRate,
} as const;

export const PERSONAL_OVERRIDE_TIERS = tenantConfig.yield.overrideTiers;

export function getPersonalOverrideTier(mainUSD: number, numPersonals: number) {
  if (numPersonals <= 0) return null;
  const unlocked = PERSONAL_OVERRIDE_TIERS.filter((t) => mainUSD >= t.mainUSD);
  if (unlocked.length === 0) return null;
  const idx = Math.min(numPersonals, unlocked.length) - 1;
  return unlocked[idx];
}

export const TRUST_METRICS = {
  CERTIK_SCORE:      tenantConfig.trust.certikScore,
  TREASURY_BACKING:  tenantConfig.trust.treasuryBacking,
} as const;

export const ONBOARDING_STEPS = [
  { id: 1, title: 'Connect Wallet',    description: 'Connect any Web3 wallet' },
  { id: 2, title: 'Validate Network',  description: "Ensure you're on Polygon network" },
  { id: 3, title: 'Verify Position',   description: 'We detect your sLGNS staking position' },
  { id: 4, title: 'Generate Referral', description: 'Get your personal referral link' },
  { id: 5, title: 'Setup Sub-Wallets', description: 'Add additional wallets to track' },
  { id: 6, title: 'Complete',          description: 'Join the community and start earning' },
] as const;

export type FeatureGate = keyof typeof tenantConfig.features | null;

export const NAV_LINKS: ReadonlyArray<{
  href: string;
  label: string;
  protected: boolean;
  featureGate: FeatureGate;
}> = [
  { href: '/',            label: 'Home',        protected: false, featureGate: null },
  { href: '/dashboard',  label: 'Dashboard',    protected: true,  featureGate: null },
  { href: '/onboarding', label: 'Get Started',  protected: false, featureGate: null },
  { href: '/community',  label: 'Community',    protected: false, featureGate: 'communityChannels' },
  { href: '/guide',      label: 'Guide',        protected: false, featureGate: null },
];

export const WALLET_NAMES: Record<string, string> = {
  [WALLETS.APEX_COMMAND.toLowerCase()]: 'Apex Command (Main)',
  [WALLETS.SUB_1.toLowerCase()]:        'Sub-Wallet 1',
  [WALLETS.SUB_2.toLowerCase()]:        'Sub-Wallet 2',
  [WALLETS.SUB_3.toLowerCase()]:        'Sub-Wallet 3',
};

export function getWalletName(address: string): string {
  return WALLET_NAMES[address.toLowerCase()] || 'Unknown Wallet';
}

export function isAdminWallet(address: string | undefined): boolean {
  if (!address) return false;
  return tenantConfig.wallets.admin.includes(address.toLowerCase());
}

export const SUBSCRIPTION = {
  get PAYMENT_WALLET() { return (tenantConfig.wallets.payment || import.meta.env.VITE_PAYMENT_WALLET || '') as `0x${string}`; },
  get PLANS() {
    return {
      MONTHLY: { id: 'monthly' as const, label: 'Monthly', priceUSD: tenantConfig.subscription.monthlyPriceUSD, durationDays: 30 },
      ANNUAL: { id: 'annual' as const, label: 'Annual', priceUSD: tenantConfig.subscription.annualPriceUSD, durationDays: 365 },
      LIFETIME: { id: 'lifetime' as const, label: 'Lifetime', priceUSD: tenantConfig.subscription.lifetimePriceUSD, durationDays: Infinity },
      INTRO: { id: 'monthly' as const, label: 'Intro Monthly', priceUSD: tenantConfig.subscription.introPriceUSD, durationDays: 30 },
    };
  },
  get GRACE_PERIOD_DAYS() { return tenantConfig.subscription.gracePeriodDays; },
  RENEWAL_REMINDER_DAYS: 7,
  RENEWAL_URGENT_DAYS: 3,
  get ACCEPTED_TOKENS() { return tenantConfig.subscription.acceptedTokens; },
  FREE_FEATURES: ['calculator', 'marketing', 'community', 'onboarding', 'landing'] as const,
  PREMIUM_FEATURES: ['dashboard', 'yield-wizard', 'history', 'team-tracker', 'yield-planner', 'balance-chart'] as const,
};

export const PROJECTION_DAYS = [30, 60, 90] as const;

export function formatUSD(value: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatNumber(value: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function truncateAddress(address: string, start = 6, end = 4): string {
  if (!address || address.length < start + end + 3) return address;
  return `${address.slice(0, start)}...${address.slice(-end)}`;
}
